import { useEffect, useState } from "react";

type DebounceOptions<T> = {
  delayMs?: number;
  onChange?: (newValue: T) => void;
};

/**
 * Returns a debounced version of the input value that only updates after the specified delay has passed
 * without the value changing. Useful for delaying actions like API calls or filtering until the user
 * has stopped typing or interacting for a certain period.
 *
 * @template T - The type of the value to debounce
 * @param value - The value to debounce
 * @param delayMs - The debounce delay in milliseconds
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, options: DebounceOptions<T> = {}): T {
  const { delayMs = 300, onChange = () => {} } = options;
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      onChange(value);
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delayMs, onChange]);

  return debouncedValue;
}
